'use client';

import { Popover as PopoverPrimitive } from '@base-ui/react/popover';
import * as React from 'react';
import {
  usePortalContainerOverride,
  usePortalReplacementRef,
} from '@/components/ui/portal-container';
import { useControllableState } from '@/hooks/use-controllable-state';

import { cn } from '@/lib/utils';

// ============================================
// Base Popover Components (from ui/popover.tsx)
// ============================================

function PopoverRoot({ ...props }: PopoverPrimitive.Root.Props) {
  return <PopoverPrimitive.Root data-slot="popover" {...props} />;
}

function PopoverTrigger({ ...props }: PopoverPrimitive.Trigger.Props) {
  return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />;
}

function PopoverPopup({
  className,
  align = 'center',
  alignOffset = 0,
  side = 'bottom',
  sideOffset = 4,
  anchor,
  collisionAvoidance,
  positionMethod,
  container,
  ...props
}: PopoverPrimitive.Popup.Props &
  Pick<
    PopoverPrimitive.Positioner.Props,
    | 'align'
    | 'alignOffset'
    | 'side'
    | 'sideOffset'
    | 'anchor'
    | 'collisionAvoidance'
    | 'positionMethod'
  > & {
    /**
     * DOM node to portal the popup into. Falls back to
     * `usePortalContainerOverride()` (which checks `PortalContainerProvider`), then to
     * the base-ui default (the document body).
     */
    container?: HTMLElement | null;
  }) {
  const resolvedContainer = usePortalContainerOverride(container);
  const initialFocusTarget = React.useRef<HTMLDivElement | false | null>(null);
  const { ref: popupRef, finalFocus } = usePortalReplacementRef(
    resolvedContainer,
    props.ref,
    initialFocusTarget,
    props.finalFocus,
  );
  return (
    <PopoverPrimitive.Portal container={resolvedContainer}>
      <PopoverPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
        anchor={anchor}
        collisionAvoidance={collisionAvoidance}
        positionMethod={positionMethod}
        className="isolate z-50"
      >
        <PopoverPrimitive.Popup
          data-slot="popover-popup"
          className={cn(
            'bg-popover text-popover-foreground motion-safe:transition-opacity motion-reduce:transition-none motion-safe:duration-(--duration-fast) motion-safe:ease-standard motion-safe:data-starting-style:opacity-0 motion-safe:data-ending-style:opacity-0 ring-foreground/10 flex flex-col gap-4 rounded-md p-4 text-sm shadow-lg ring-1 z-50 w-72 origin-(--transform-origin) outline-hidden',
            className,
          )}
          {...props}
          finalFocus={finalFocus}
          initialFocus={(interaction) => {
            if (initialFocusTarget.current === false) return false;
            if (typeof props.initialFocus === 'function')
              return props.initialFocus(interaction);
            if (typeof props.initialFocus === 'object')
              return props.initialFocus.current;
            return (
              props.initialFocus ??
              (interaction === 'touch' ? initialFocusTarget.current : true)
            );
          }}
          ref={popupRef}
        />
      </PopoverPrimitive.Positioner>
    </PopoverPrimitive.Portal>
  );
}

function PopoverHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="popover-header"
      className={cn('flex flex-col gap-1 text-sm', className)}
      {...props}
    />
  );
}

function PopoverTitle({ className, ...props }: PopoverPrimitive.Title.Props) {
  return (
    <PopoverPrimitive.Title
      data-slot="popover-title"
      className={cn('font-medium', className)}
      {...props}
    />
  );
}

function PopoverDescription({
  className,
  ...props
}: PopoverPrimitive.Description.Props) {
  return (
    <PopoverPrimitive.Description
      data-slot="popover-description"
      className={cn('text-muted-foreground', className)}
      {...props}
    />
  );
}

// ============================================
// Popover Wrapper (with controlled/uncontrolled support)
// ============================================

export interface PopoverProps
  extends Omit<
    React.ComponentProps<typeof PopoverRoot>,
    'onOpenChange' | 'children'
  > {
  /** The element that triggers the popover (required) */
  trigger: React.ReactElement;
  /** Popover content (typically Popover.Popup component) */
  children?: React.ReactNode;
  /** Controlled open state. When provided, component is controlled. */
  open?: boolean;
  /** Initial open state for uncontrolled mode */
  defaultOpen?: boolean;
  /** Callback when open state changes */
  onOpenChange?: (open: boolean) => void;
}

function Popover({
  trigger,
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange,
  children,
  ...props
}: PopoverProps) {
  const [open, handleOpenChange] = useControllableState({
    value: controlledOpen,
    defaultValue: defaultOpen,
    onChange: onOpenChange,
  });

  return (
    <PopoverRoot open={open} onOpenChange={handleOpenChange} {...props}>
      <PopoverTrigger render={trigger} />
      {children}
    </PopoverRoot>
  );
}

// Type exports
export type PopoverTriggerProps = PopoverPrimitive.Trigger.Props;
export type PopoverPopupProps = PopoverPrimitive.Popup.Props &
  Pick<
    PopoverPrimitive.Positioner.Props,
    | 'align'
    | 'alignOffset'
    | 'side'
    | 'sideOffset'
    | 'anchor'
    | 'collisionAvoidance'
    | 'positionMethod'
  > & {
    container?: HTMLElement | null;
  };
export type PopoverHeaderProps = React.ComponentProps<'div'>;
export type PopoverTitleProps = PopoverPrimitive.Title.Props;
export type PopoverDescriptionProps = PopoverPrimitive.Description.Props;

Popover.displayName = 'Popover';
PopoverRoot.displayName = 'Popover.Root';
PopoverTrigger.displayName = 'Popover.Trigger';
PopoverPopup.displayName = 'Popover.Popup';
PopoverHeader.displayName = 'Popover.Header';
PopoverTitle.displayName = 'Popover.Title';
PopoverDescription.displayName = 'Popover.Description';

/**
 * Popover component with dot notation for compound parts.
 *
 * @example
 * ```tsx
 * <Popover trigger={<Button>Open</Button>}>
 *   <Popover.Popup>
 *     <Popover.Header>
 *       <Popover.Title>Title</Popover.Title>
 *       <Popover.Description>Description</Popover.Description>
 *     </Popover.Header>
 *   </Popover.Popup>
 * </Popover>
 * ```
 */
const PopoverCompound = Object.assign(Popover, {
  Root: PopoverRoot,
  Trigger: PopoverTrigger,
  Popup: PopoverPopup,
  Header: PopoverHeader,
  Title: PopoverTitle,
  Description: PopoverDescription,
});

export {
  PopoverCompound as Popover,
  PopoverDescription,
  PopoverHeader,
  PopoverPopup,
  PopoverRoot,
  PopoverTitle,
  PopoverTrigger,
};
